import * as R from 'ramda'
import { RSAA } from 'redux-api-middleware'
import { getApiPath, webStorage } from 'sidecar-utility-library'
import { arrayToOrderedMap } from './utils'

import { initialState, types } from 'store/Applicant'
import { siftApplicants, cleanTempApplicants } from 'utils/applicants'

/** Fetches the applicants for a given account. */
export const fetchApplicants = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      `acct`,
      `accounts/${webStorage.getItem('aud')}/applications`,
    ),
    method: 'GET',
    types: [
      types.FETCH_APPLICANTS_REQUEST,
      types.FETCH_APPLICANTS_SUCCESS,
      types.FETCH_APPLICANTS_ERROR,
    ],
  },
})

/**
 * Saves applicant data to the database. This will not return the applicants
 * quote unless the quote has a UUID.
 */
export const saveApplicants = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      `acct`,
      `accounts/${webStorage.getItem('aud')}/applications`,
    ),
    method: 'POST',
    body: ({ Applicant }) => {
      const applicants = Object.values(Applicant.applicants.entities)
      return JSON.stringify({
        applicants: cleanTempApplicants(applicants),
      })
    },
    types: [
      types.SAVE_APPLICANTS_REQUEST,
      types.SAVE_APPLICANTS_SUCCESS,
      types.SAVE_APPLICANTS_ERROR,
    ],
  },
})

/**
 * Deletes an applicant from the database.
 *
 * @param {str} applicantUuid
 */
export const deleteApplicant = applicantUuid => ({
  [RSAA]: {
    endpoint: getApiPath(`acct`, `applicants/${applicantUuid}`),
    method: 'DELETE',
    types: [
      types.DELETE_APPLICANT_REQUEST,
      {
        type: types.DELETE_APPLICANT_SUCCESS,
        meta: { applicantUuid },
      },
      types.DELETE_APPLICANT_ERROR,
    ],
  },
})

export const fetchSSN = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      `acct`,
      `accounts/${webStorage.getItem('aud')}/verify`,
    ),
    method: 'GET',
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    types: [
      types.FETCH_SSN_REQUEST,
      types.FETCH_SSN_SUCCESS,
      types.FETCH_SSN_ERROR,
    ],
  },
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.FETCH_APPLICANTS_REQUEST:
    case types.SAVE_APPLICANTS_REQUEST:
    case types.DELETE_APPLICANT_REQUEST: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: true,
          error: '',
        },
      }
    }
    case types.FETCH_APPLICANTS_SUCCESS: {
      const { applicants } = payload
      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          entities: {
            ...state.applicants.entities,
            ...applicants.reduce(
              (acc, applicant) => ({
                ...acc,
                [applicant.uuid]: {
                  ...state.applicants.entities[applicant.uuid],
                  ...R.mergeDeepRight(
                    state.applicants.entities[applicant.uuid] || {},
                    applicant,
                  ),
                },
              }),
              {},
            ),
          },
        },
      }
      return siftApplicants(nextState)
    }
    case types.FETCH_APPLICANTS_ERROR:
    case types.SAVE_APPLICANTS_ERROR: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    case types.SAVE_APPLICANTS_SUCCESS: {
      const { applicants } = payload
      const currentApplicants = state.applicants.entities

      // extract the subproduct from the applicants
      // merges data from temporary applicants into their corresponding
      // saved versions. Used to keep data that backend doesn't return back.
      const savedApplicants = applicants.map(savedApplicant => {
        const currentApplicant =
          currentApplicants[savedApplicant.tempUuid || savedApplicant.uuid]
        const newApplicant = {
          ...currentApplicant,
          ...savedApplicant,
          quote: {
            ...currentApplicant?.quote,
          },
        }
        return R.omit(['tempUuid'], newApplicant)
      })

      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          entities: arrayToOrderedMap('uuid')(savedApplicants),
        },
      }
      return siftApplicants(nextState)
    }
    case types.DELETE_APPLICANT_SUCCESS: {
      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          entities: R.omit([meta.applicantUuid], state.applicants.entities),
        },
      }
      return siftApplicants(nextState)
    }
    case types.DELETE_APPLICANT_ERROR: {
      if (R.propEq('status', 412)(payload)) {
        return {
          ...state,
          applicants: {
            ...state.applicants,
            busy: false,
          },
        }
      } else {
        return {
          ...state,
          applicants: {
            ...state.applicants,
            busy: false,
            error: R.pathOr(
              'Something went wrong',
              ['response', 'message'],
              payload,
            ),
          },
        }
      }
    }

    case types.FETCH_SAVED_QUOTE_REQUEST: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: true,
          isFetchedQuote: false,
          fetchingQuote: true,
          error: '',
        },
      }
    }
    case types.FETCH_SAVED_QUOTE_SUCCESS: {
      // associate the email to the primary applicant
      const primaryApplicant = R.pipe(
        R.prop('applicants'),
        R.find(R.propEq('primary', true)),
        primary => R.assoc('email', R.prop('email', payload), primary),
      )(payload)

      const customQuote = R.pipe(
        R.prop('packages'),
        R.filter(R.propEq('packageName', 'customizedPackage')),
      )(payload)

      const _applicants = R.pipe(
        R.prop('applicants'),
        R.map(a => (a.primary ? primaryApplicant : a)),
      )(payload)

      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          fetchingQuote: false,
          isFetchedQuote: true,
          entities: arrayToOrderedMap('uuid')(_applicants),
        },
        packages: {
          ...state.packages,
          customQuote: customQuote,
        },
      }
      return siftApplicants(nextState)
    }
    case types.FETCH_SAVED_QUOTE_ERROR: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          isFetchedQuote: false,
          fetchingQuote: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    case types.FETCH_SSN_REQUEST: {
      return {
        ...state,
        test: {
          ...state.test,
          busy: true,
          error: '',
        },
      }
    }
    case types.FETCH_SSN_SUCCESS: {
      const applicants = { ...state.applicants.entities }

      payload.forEach(({ uuid, ssnLast4 = null }) => {
        if (ssnLast4 && applicants[uuid]) {
          applicants[uuid] = { ...applicants[uuid], ssnLast4 }
        }
      })

      return {
        ...state,
        test: {
          ...state.test,
          busy: false,
        },
        applicants: {
          ...state.applicants,
          entities: applicants,
        },
      }
    }
    case types.FETCH_SSN_ERROR: {
      return {
        ...state,
        test: {
          ...state.test,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    default:
      return state
  }
}
